import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "aws-amplify";
import "./ProductDetail.css"; // Ensure the CSS is linked

const ProductDetail = () => {
  const { asin } = useParams(); // Get product ASIN from URL
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchProduct() {
      try {
        setLoading(true);
        setError(null);

        // Step 1: Check localStorage for products
        const cachedProducts = JSON.parse(localStorage.getItem("products")) || [];
        const cachedProduct = cachedProducts.find((p) => p.ASIN === asin);

        if (cachedProduct) {
          console.log("Product found in localStorage:", cachedProduct); // Debug log
          setProduct(cachedProduct);
          updateMetaTags(cachedProduct);
          setLoading(false);
          return;
        }

        // Step 2: If not found in localStorage, fetch all deals for the category
        console.log("Product not found in localStorage. Fetching all deals..."); // Debug log
        const category = localStorage.getItem("currentCategory") || "hotdeals"; // Fallback category
        const dealsData = await API.get("DealsSiteAPI", `/api/deals/${category}`, {});
        const deals = dealsData.deals || [];

        // Step 3: Find the product by ASIN in the fetched deals
        const foundProduct = deals.find((p) => p.ASIN === asin);

        if (foundProduct) {
          console.log("Product found in fetched deals:", foundProduct); // Debug log
          setProduct(foundProduct);
          updateMetaTags(foundProduct);
          localStorage.setItem("products", JSON.stringify(deals)); // Cache the fetched deals
        } else {
          console.log("Product not found in fetched deals"); // Debug log
          setError("Product not found.");
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching product:", error); // Debug log
        setError("Failed to load product. Please try again later.");
        setLoading(false);
      }
    }

    fetchProduct();
  }, [asin]);

  const updateMetaTags = (product) => {
    if (product) {
      document.title = product.Title;

      // Update Open Graph Meta Tags
      updateMetaTag("og:title", product.Title);
      updateMetaTag("og:description", product.Description || "Check out this great deal!");
      updateMetaTag("og:image", product.Image);
      updateMetaTag("og:url", window.location.href);

      // Update Twitter Meta Tags
      updateMetaTag("twitter:title", product.Title);
      updateMetaTag("twitter:description", product.Description || "Check out this great deal!");
      updateMetaTag("twitter:image", product.Image);
    }
  };

  const updateMetaTag = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("property", property);
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", content);
  };

  if (loading) {
    return <p className="product-detail-loading">Loading product details...</p>;
  }

  if (error) {
    return <p className="product-detail-error">{error}</p>;
  }

  if (!product) {
    return <p className="product-detail-error">Product not found.</p>;
  }

  return (
    <div className="product-detail-wrapper">
      {/* Left side: Product image */}
      <div className="product-detail-image-section">
        <img
          src={product.Image}
          alt={product.Title}
          className="product-detail-image"
        />
      </div>

      {/* Right side: Product info */}
      <div className="product-detail-content">
        <h1 className="product-detail-heading">{product.Title}</h1>
        <p className="product-detail-price-section">
          <span className="product-detail-sale-price">{product.Price}</span>
          <span className="product-detail-original-price">{product.ActualPrice}</span>
          <span className="product-detail-discount">{product.DiscountPercentage}% off</span>
        </p>
        <div className="product-detail-buy-button">
          <a
            href={product.DetailPageURL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Buy on Amazon
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
